import SiteFooter from '../../components/layout/SiteFooter';
import { useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import usePageTitle from '../../hooks/usePageTitle';
import {
    Check, ArrowRight, X, SlidersHorizontal, Loader2, ZoomIn,
} from 'lucide-react';
import Illustration from '../../components/ui/Illustration';
import illuThemes from '../../assets/illustrations/menu/thematiques.svg?raw';
import illuConfig from '../../assets/illustrations/menu/configuration-thematiques.svg?raw';
import illuAcces from '../../assets/illustrations/fonctionnalites/cle-acces.svg?raw';
import illuQuiz from '../../assets/illustrations/choisir-un-sujet.svg?raw';
import illuMelange from '../../assets/illustrations/me-tester-sur-tout.svg?raw';
import illuRedaction from '../../assets/illustrations/quiz-libre.svg?raw';
import illuNotions from '../../assets/illustrations/approfondir-une-notion.svg?raw';
import illuFiche from '../../assets/illustrations/fonctionnalites/document.svg?raw';
import illuPointsFaibles from '../../assets/illustrations/travailler-mes-points-faibles.svg?raw';
import illuResultats from '../../assets/illustrations/fonctionnalites/historique.svg?raw';
import illuAvancement from '../../assets/illustrations/menu/mon-avancement.svg?raw';
import illuGuide from '../../assets/illustrations/apprentissage-guide.svg?raw';
import illuBilan from '../../assets/illustrations/bilan-cible.svg?raw';
import illuDate from '../../assets/illustrations/fonctionnalites/revision-espacee.svg?raw';
import illuPerso from '../../assets/illustrations/fonctionnalites/generation-ia.svg?raw';
import illuPartage from '../../assets/illustrations/fonctionnalites/gestion-codes.svg?raw';
import illuCode from '../../assets/illustrations/menu/activer-un-code.svg?raw';
import illuFormateur from '../../assets/illustrations/menu/espace-formateur.svg?raw';

// Table des matières (ancres vers chaque chapitre)
const CHAPTERS = [
    { id: 'demarrer', label: 'Démarrer', detail: 'Ton espace et tes accès', illustration: illuThemes },
    { id: 'sentrainer', label: "S'entraîner", detail: 'Bilans, quiz, fiches et résultats', illustration: illuGuide },
    { id: 'creer-partager', label: 'Créer & partager', detail: 'Tes documents et tes invités', illustration: illuPerso },
];

// Invalide aussi les anciennes captures conservées dans le cache du navigateur.
const MEDIA_VERSION = '2026-10-27';

// Guide de bienvenue complet : une section par fonctionnalité, image/texte en
// quinconce, regroupées en 3 chapitres. Présenté une fois aux nouveaux inscrits
// (flag serveur onboarded_at), puis ré-accessible via le compte. Chaque section
// charge un média déposé dans public/guide/<key>.png ou, pour les parcours qui
// gagnent à montrer une transition, un clip déclaré avec `media: 'video'`.
// À défaut, le texte reste accessible avec le pictogramme de la fonctionnalité.
const SECTIONS = [
    // ===== Chapitre 1 — Démarrer =====
    {
        chapter: 'Démarrer', chapterId: 'demarrer',
        key: '01-themes', color: '#7c3aed', illustration: illuThemes,
        eyebrow: 'Pour commencer',
        title: 'Tes thématiques, au même endroit',
        text: "Dès la connexion, le bandeau du haut réunit toutes tes thématiques : les concours et certifications auxquels tu as accès, tes thématiques perso, et celles qu'on a partagées avec toi. Un clic sur l'une d'elles ouvre son espace d'entraînement.",
        bullets: [
            'Concours et certifications validés (3 IA + relecture humaine)',
            'Tes thématiques perso générées par IA',
            "Celles qu'un proche ou un formateur a partagées avec toi",
            "Le bandeau du haut te fait passer de l'une à l'autre en un clic",
        ],
    },
    {
        key: '02-affichage', color: '#2563eb', illustration: illuConfig,
        eyebrow: 'Personnalisation',
        title: 'Choisis ce qui apparaît dans ton espace',
        text: "Depuis « Configuration des thématiques » (menu de ton compte), tu décides quelles thématiques apparaissent dans ton bandeau et dans quel ordre, pour garder en avant celles qui comptent vraiment pour toi.",
        bullets: [
            'Sélectionne les thématiques à afficher dans ton espace',
            "Écran « Ordre d'affichage » : réordonne par glisser-déposer",
            "Masque celles que tu n'utilises pas — rien n'est perdu",
            'Bascule entre thème clair et sombre (icône en haut)',
        ],
    },
    {
        key: '03-abonnement', color: '#059669', illustration: illuAcces,
        eyebrow: 'Accès',
        title: 'Essaie gratuitement, abonne-toi si ça te plaît',
        text: "Chaque thématique propose des sujets d'essai gratuits pour tester avant de t'engager. Pour tout débloquer, un abonnement par thématique — paiement unique, sans renouvellement automatique.",
        bullets: [
            "Des sujets d'essai gratuits dans chaque thématique",
            'Abonnement 1, 6 ou 12 mois selon la thématique',
            "Prolongeable : le temps acheté s'ajoute à ton accès",
            'Et parfois une thématique entière offerte pour un temps (badge 🎁)',
        ],
    },

    // ===== Chapitre 2 — S'entraîner =====
    {
        chapter: "S'entraîner", chapterId: 'sentrainer',
        key: 'prochaine-etape', color: '#6546ed', illustration: illuGuide,
        eyebrow: 'Ta prochaine étape',
        title: 'Un conseil pour commencer, des choix pour continuer',
        text: "Dans ta thématique, « Ta prochaine étape » propose une séance adaptée à tes résultats : un premier bilan, les révisions du jour ou un sujet prioritaire. Tu peux la lancer directement ou choisir toi-même un mode de travail.",
        bullets: [
            '« Démarrer le bilan » : 16 questions maximum pour faire le point',
            'Un bouton qui annonce le nombre de révisions à lancer',
            '« Voir mon parcours guidé » pour consulter tes priorités',
            'Choisis ton concours si la thématique en prépare plusieurs',
            'Ou choisis un sujet, un mélange, tes points faibles, la rédaction ou les notions disponibles',
        ],
    },
    {
        key: 'date-ecrit', color: '#6546ed', illustration: illuDate,
        eyebrow: 'Ma date d’écrit',
        title: 'Garde ton échéance en vue',
        text: 'Dans l’en-tête de ta thématique, « Ma date d’écrit » permet de choisir une date du calendrier, lorsqu’elle est proposée, ou de saisir la tienne. Le compte à rebours et la phase de préparation restent ensuite visibles.',
        bullets: [
            'Choisis la date correspondant à ton concours',
            'Apprentissage, consolidation ou stabilisation selon le temps restant',
            'Tu peux aussi répondre « Je ne sais pas encore »',
            'Rouvre le bouton pour modifier ou retirer la date',
        ],
    },
    {
        key: '04-quiz', color: '#0891b2', illustration: illuQuiz,
        eyebrow: 'Choisir un sujet · Mode Quiz',
        title: 'Lance un quiz sur le sujet de ton choix',
        text: "Clique sur « Choisir un sujet », puis « Voir les sujets ». Les sujets sont regroupés par catégorie, avec une recherche et leur progression. Choisis un sujet, le nombre de questions et ton mode ; chaque réponse est sauvegardée pour reprendre plus tard.",
        bullets: [
            'Mode Apprentissage : correction immédiate + explication',
            'Mode Classique : toutes les questions, puis le résultat',
            'Choisis 10, 20, 30 questions… ou toutes',
            'Recherche par sujet et « Reprise rapide » des sessions en cours',
            'Une barre de progression par catégorie : vu, maîtrisé, restant',
        ],
    },
    {
        key: '05-melange', color: '#d97706', illustration: illuMelange,
        eyebrow: 'Me tester sur tout · Mode Mélange',
        title: 'Mélange les sujets pour enchaîner plusieurs thèmes',
        text: "Clique sur « Me tester sur tout », puis « Configurer le mélange ». Le quiz transversal combine plusieurs sujets de ta thématique, sur le concours sélectionné si elle en propose plusieurs. Choisis un démarrage rapide ou règle ta sélection.",
        bullets: [
            'Démarrages rapides : Express (10), Standard (20), Marathon (50), Surprise',
            'Ou personnalise : nombre de questions, difficulté, catégories',
            'Aperçu en direct de la sélection et du pool disponible',
            'Plusieurs tentatives possibles en parallèle, toutes reprenables',
        ],
    },
    {
        key: 'qrc', color: '#be123c', illustration: illuRedaction,
        eyebrow: 'Répondre à des questions courtes · Mode Rédaction',
        title: 'Rédige ta réponse, elle est corrigée point par point',
        text: "La carte « Répondre à des questions courtes » apparaît quand la thématique en propose. Clique sur « Voir les questions » : l'atelier réunit la liste par sujet et une zone pour rédiger avec tes mots. La correction compare ta réponse aux attendus du barème.",
        bullets: [
            'Un espace Rédaction dédié, avec navigation entre les questions',
            'Verdict détaillé : bien traité, partiellement, manquant, contredit',
            'Un score et un seuil de réussite affichés clairement',
            'Un exemple déjà corrigé offert sur certains sujets, avant abonnement',
            'Solde de corrections et recharge affichés pour les thématiques à crédits',
            'Retrouve tes corrections dans Mes résultats, section « Réponses rédigées »',
        ],
    },
    {
        key: 'notions', color: '#7f1d1d', illustration: illuNotions,
        eyebrow: 'Approfondir une notion · À la carte',
        title: 'Une fiche pour comprendre, 20 questions pour vérifier',
        text: "Sur la préparation DGFiP, l'onglet Notions réunit des fiches pour comprendre l'essentiel d'un sujet, chacune suivie de 20 questions pour vérifier ce que tu as retenu. La fiche se lit partie par partie. Les notions sont en plus de la thématique : à l'unité ou en pack.",
        bullets: [
            "Une fiche découpée en parties, le test s'ouvre une fois la fiche lue",
            '« Revoir dans la fiche » sous chaque explication, pour retrouver la partie qui répond',
            'Repère les guides gratuits dans la liste des notions',
            "À l'unité ou en pack (5 ou 10 notions au choix, moins cher par notion)",
            "Une notion offerte avec l'abonnement 6 mois, deux avec 12 mois",
            'Tes séances de test dans Mes résultats, section « Notions »',
        ],
        link: { label: 'Voir les notions', to: '/compte/quiz/fonction-publique-etat?onglet=notions' },
    },
    {
        key: 'fiche-notion', color: '#7f1d1d', illustration: illuFiche,
        eyebrow: 'Lire une fiche',
        title: 'Lis à ton rythme, puis vérifie tes acquis',
        text: 'Ouvre une notion, puis « Lire la fiche » ou le bouton de reprise. Le lecteur présente une partie à la fois, avec des repères pour revenir sur ce que tu as déjà lu. Une fois la fiche terminée, tu peux lancer son test.',
        bullets: [
            'Passe d’une partie à l’autre avec Précédente et Suivante',
            'Retrouve ta progression quand tu rouvres la notion',
            'Choisis une correction immédiate ou le résultat à la fin du test',
            'Depuis une correction, « Revoir dans la fiche » retrouve le passage utile',
        ],
    },
    {
        key: '06-progression', color: '#4f46e5', illustration: illuPointsFaibles,
        eyebrow: 'Travailler mes points faibles · Mode Entraînement',
        title: 'Cible tes erreurs et consolide tes acquis',
        text: "Clique sur « Travailler mes points faibles », puis « Voir les entraînements ». Cet espace rassemble les révisions espacées, les outils d'entraînement ciblé et ta carte de maîtrise, à partir de tes premières réponses.",
        bullets: [
            'Révisions espacées : « Choisir ma séance » parmi les questions dues',
            'Choisir mon entraînement : quiz intelligent, mes faiblesses, consolidation',
            'Cibler mes erreurs : jamais réussies, instables, erreurs récentes',
            'Ma maîtrise : réussite par catégorie et notions les plus faibles',
        ],
    },
    {
        key: '07-resultats', color: '#16a34a', illustration: illuResultats,
        eyebrow: 'Onglet Mes résultats',
        title: 'Relis, compare, mesure',
        text: "À la fin de chaque session : score, corrections détaillées et verdict de maîtrise. Dans « Mes résultats », choisis « Revoir mes séances » pour l'historique ou « Comprendre ma progression » pour les statistiques.",
        bullets: [
            'Correction avec explication par question',
            '« Voir mes séances » : historique par date ou par score, avec consultation et reprise',
            '« Voir mon bilan » : statistiques, tendance sur 30 jours et couverture du contenu',
            'Vue « Suivi des résultats » quand tu as partagé ta thématique',
            'Refais une série quand tu veux',
        ],
    },
    {
        key: 'avancement', color: '#7c3aed', illustration: illuAvancement,
        eyebrow: 'Mon avancement',
        title: "Ta vue d'ensemble, toutes thématiques réunies",
        text: "Depuis le menu de ton compte, « Mon avancement » rassemble toutes tes thématiques sur une seule page : où tu en es, ce qui avance, ce qui dort — et ce qu'il reste à réviser aujourd'hui.",
        bullets: [
            'Un taux de réussite et des étoiles de maîtrise par thématique',
            'Un statut clair : en cours, à consolider, bien maîtrisé, en pause',
            'Le nombre de révisions dues, thématique par thématique',
            'La date de fin de chaque accès (abonnement, partage, gratuité)',
        ],
    },
    {
        key: '08-guide', color: '#0d9488', illustration: illuGuide,
        eyebrow: 'Voir mon parcours guidé',
        title: 'Laisse-toi guider',
        text: "Depuis « Voir mon parcours guidé », retrouve ta séance conseillée et les sujets à prioriser. Le bilan commence par 16 questions maximum, privilégie celles que tu n'as jamais vues et s'enrichit au fil de tes séances.",
        bullets: [
            'Premier bilan, exploration de la suite et révisions selon tes résultats',
            "Le bouton « Je ne sais pas » pour ne pas fausser le diagnostic",
            'Bilan ciblé : choisis les domaines à évaluer, jusqu’à 20 questions',
            'Sujets à travailler, fragiles, solides ou encore non évalués',
            'Disponible sur les thématiques éditoriales et perso',
        ],
    },
    {
        key: 'bilan-cible', color: '#0d9488', illustration: illuBilan,
        eyebrow: 'Bilan ciblé',
        title: 'Fais le point sur les domaines de ton choix',
        text: 'Dans le parcours guidé, « Choisir moi-même les domaines » ouvre le bilan ciblé. Sélectionne ce que tu veux évaluer : le nombre de questions s’adapte à ta sélection, jusqu’à 20 questions.',
        bullets: [
            'Sélectionne un ou plusieurs domaines',
            'Vérifie le nombre de questions avant de lancer le bilan',
            'Le compte gratuit utilise uniquement les sujets accessibles gratuitement',
            'Les domaines réservés à l’accès complet restent indiqués comme non évalués',
        ],
    },

    // ===== Chapitre 3 — Créer & partager =====
    {
        chapter: 'Créer & partager', chapterId: 'creer-partager',
        key: '09-perso', color: '#db2777', illustration: illuPerso,
        eyebrow: 'Mes thématiques',
        title: 'Transforme tes documents en QCM',
        text: "Dans « Mes thématiques », sélectionne ta thématique perso et ouvre « Gestion des sujets ». Ajoute un document (PDF, Word, image…), plusieurs documents ou un thème décrit à l'IA pour générer tes QCM. Tu peux aussi compléter un sujet existant.",
        bullets: [
            'Document, multi-documents ou thème décrit → 10 à 50 questions',
            "Des consignes libres pour orienter la génération (angle, niveau, format)",
            '30 générations IA par abonnement, + questions complémentaires si besoin',
            '« Sujet vide » pour ajouter toi-même tes questions sans génération IA',
            'Catégories, édition, régénération et suivi de tes générations',
            'Gestion par code : importer ou donner une copie éditable de ta thématique',
        ],
    },
    {
        key: '10-partage', color: '#ea580c', illustration: illuPartage,
        eyebrow: 'Partage & suivi',
        title: 'Partage et suis tes invités',
        text: "Dans ta thématique perso, ouvre « Gérer les partages » pour choisir le type d'accès et distribuer tes codes. Un code de partage est offert avec chaque abonnement. Le suivi de tes invités se retrouve aussi dans « Mes résultats ».",
        bullets: [
            'Code thématique : accès complet en lecture seule (compte requis)',
            'Code catégorie ou mono-sujet : jouable sans créer de compte',
            'Lien d’entraînement libre : accès anonyme à un sujet, avec compteurs d’utilisation',
            'Suis les quiz complétés et le taux de réussite, par personne ou par code',
            'Publie ta thématique si tu veux la proposer à tous',
        ],
    },
    {
        key: '11-code', color: '#0ea5e9', illustration: illuCode,
        eyebrow: 'Activer un code',
        title: "Utiliser un code que l'on m'a partagé",
        text: "Depuis « Activer un code » dans le menu de ton compte, choisis « Code cadeau » pour un abonnement offert ou un code formateur, et « Code thématique (partage) » pour une thématique perso partagée. L'accès activé apparaît dans ton espace.",
        bullets: [
            'Ouvre « Activer un code » depuis le menu de ton compte',
            'Saisis le code reçu (par lien ou par email)',
            'Pour un code formateur, confirme ton accord au suivi de tes résultats',
            "La thématique s'ajoute dans le bandeau du haut",
            'Entraîne-toi et suis tes résultats — sans pouvoir la modifier',
        ],
    },

    // Espace formateur (éditorial) — distinct du partage perso : suivi nominatif d'une promo.
    {
        key: '12-formateur', color: '#9333ea', illustration: illuFormateur,
        eyebrow: 'Espace formateur',
        title: 'Encadre un groupe et suis ses résultats',
        text: "Depuis « Espace formateur » dans le menu de ton compte, achète un pack de places, retrouve tes codes et suis tes apprenants. Ils créent un compte gratuit et activent le code, sans abonnement à payer, avec leur consentement au suivi.",
        bullets: [
            'Un seul code à partager pour toute la promo (un siège par apprenant)',
            'Trois espaces : Suivi de mes apprenants, Mes codes, Acheter des codes',
            'Suivi nominatif : quiz réalisés, taux de réussite, dernière activité',
            'Groupes d’apprenants, synthèse par catégorie et attestations imprimables',
            'Limité à la thématique du code et aux apprenants consentants',
        ],
    },
];

// Média de section : les captures réelles sont le format par défaut. Les
// sections déclarées `media: 'video'` chargent leur clip, puis retombent sur la
// capture PNG en cas d'échec. Une mini-maquette CSS reste l'ultime repli.
function GuideMedia({ base, illustration, caption, mediaType = 'image' }) {
    const [stage, setStage] = useState(mediaType); // 'video' | 'image' | 'mock'
    const [zoom, setZoom] = useState(false);
    const [ready, setReady] = useState(false); // 1re frame vidéo prête → masque le loader
    const videoRef = useRef(null);
    const triggerRef = useRef(null);
    const closeRef = useRef(null);
    const mediaUrl = (extension) => `${base}.${extension}?v=${MEDIA_VERSION}`;

    // Ne lit la vidéo QUE lorsque la section est visible (et la met en pause
    // sinon) → pas 10 vidéos qui tournent en même temps.
    useEffect(() => {
        if (stage !== 'video') return;
        const el = videoRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) el.play().catch(() => {});
                else el.pause();
            },
            { threshold: 0.4 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [stage]);

    // Modal plein écran : fermeture au clavier (Échap) + verrou du scroll de fond.
    useEffect(() => {
        if (!zoom) return;
        closeRef.current?.focus();
        const onKey = (e) => {
            if (e.key === 'Escape') setZoom(false);
            if (e.key === 'Tab' && stage === 'image') {
                e.preventDefault();
                closeRef.current?.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
            triggerRef.current?.focus({ preventScroll: true });
        };
    }, [zoom, stage]);

    // Sans capture disponible, le message de repli n'est pas zoomable.
    const canZoom = stage !== 'mock';

    let media;
    if (stage === 'video') {
        media = (
            <div className="guide-video-wrap">
                <video
                    ref={videoRef}
                    className="guide-img guide-video guide-media--zoomable"
                    src={mediaUrl('mp4')}
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedData={() => setReady(true)}
                    onError={() => setStage('image')}
                />
                {!ready && (
                    <span className="guide-video-loader" aria-hidden="true">
                        <Loader2 size={26} className="spin" />
                    </span>
                )}
            </div>
        );
    } else if (stage === 'image') {
        media = (
            <img
                className="guide-img guide-media--zoomable"
                src={mediaUrl('png')}
                alt={caption}
                loading="lazy"
                width="1280"
                height="800"
                onError={() => { setStage('mock'); setZoom(false); }}
            />
        );
    } else {
        media = (
            <div className="guide-media__unavailable">
                <Illustration svg={illustration} color="#6d28d9" className="guide-section__illu" />
                <span>La capture est indisponible. Retrouve les étapes dans le texte du guide.</span>
            </div>
        );
    }

    return (
        <>
            <figure className="guide-media">
                {canZoom ? (
                    <button ref={triggerRef} type="button" className="guide-media__open" aria-haspopup="dialog"
                        aria-label={`Agrandir l’écran : ${caption}`} onClick={() => setZoom(true)}>
                        {media}
                        <span className="guide-media__zoom"><ZoomIn size={15} aria-hidden="true" /> Agrandir l’écran</span>
                    </button>
                ) : media}
                <figcaption>Écran d’exemple · {caption}</figcaption>
            </figure>
            {zoom && canZoom && createPortal(
                <div className="guide-lightbox" role="dialog" aria-modal="true" aria-label={caption} onClick={() => setZoom(false)}>
                    <button ref={closeRef} type="button" className="guide-lightbox__close" aria-label="Fermer" onClick={() => setZoom(false)}>
                        <X size={22} />
                    </button>
                    {stage === 'video' ? (
                        <video
                            className="guide-lightbox__media"
                            src={mediaUrl('mp4')}
                            autoPlay
                            loop
                            muted
                            playsInline
                            controls
                            onClick={(e) => e.stopPropagation()}
                        />
                    ) : (
                        <img
                            className="guide-lightbox__media"
                            src={mediaUrl('png')}
                            alt={caption}
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                </div>,
                document.body
            )}
        </>
    );
}

export default function Guide() {
    const isPublicPage = useLocation().pathname === '/guide';
    usePageTitle('Guide');
    const navigate = useNavigate();
    const { user, markOnboarded, dashboardPrefs } = useAuthStore();
    const [busy, setBusy] = useState(false);

    // Accessible aussi hors connexion (acquisition) : pas de redirection.
    // L'accroche et les CTA s'adaptent selon que l'utilisateur est connecté ou non.
    const go = async (to) => {
        if (busy) return;
        setBusy(true);
        if (user) await markOnboarded();
        navigate(to, user ? { state: { fromAuth: true } } : undefined);
    };

    const prenom = user?.prenom ? `, ${user.prenom}` : '';
    // 1re visite (nouvel inscrit connecté) → message de bienvenue ; sinon
    // (revisite OU visiteur non connecté) → titre récapitulatif.
    const firstVisit = user?.onboarded === false;
    // Thématique déjà posée dans l'espace ? C'est le cas des inscrits venus d'une
    // landing dédiée (cf. backend/lib/Acquisition.php) : inutile de les envoyer
    // configurer un espace qui l'est déjà — on les emmène directement dedans.
    // Le hub n'est pas une thématique posée : un détour par « Mes Thématiques »
    // ne doit pas détourner le bouton de départ de la thématique choisie.
    const lastScope = dashboardPrefs?.lastScope !== 'mes-thematiques' ? dashboardPrefs?.lastScope : null;
    const presetScope = lastScope || dashboardPrefs?.selectedScopes?.[0] || null;
    const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start',
    });

    return (
        <div className="guide-page">
            <div className="guide-inner">
                <header className="guide-hero">
                    {user && (
                        <button className="guide-hero__skip" disabled={busy} onClick={() => go(
                            !firstVisit ? '/compte/quiz/mes-thematiques'
                                : presetScope ? `/compte/quiz/${presetScope}`
                                    : '/compte/account/thematiques'
                        )}>
                            Passer le guide
                        </button>
                    )}
                    <span className="guide-badge">Guide</span>
                    <h1>{firstVisit ? `Bienvenue sur MonQuiz${prenom} 👋` : 'Prends tes repères sur MonQuiz'}</h1>
                    <p>{firstVisit
                        ? 'Un tour complet de la plateforme, fonctionnalité par fonctionnalité. Tu pourras le retrouver à tout moment depuis ton compte.'
                        : 'Choisis ton point de départ et retrouve les bons écrans pour réviser, suivre tes progrès ou créer tes propres quiz.'}</p>
                    <span className="guide-hero__note">Les captures présentent un profil d’exemple. Les sujets et les options disponibles dépendent de ta thématique et de ton accès.</span>
                </header>

                <nav className="guide-toc" aria-label="Chapitres du guide">
                    {CHAPTERS.map((c, index) => (
                        <button key={c.id} type="button" className="guide-toc__item" onClick={() => scrollToId(c.id)}>
                            <Illustration svg={c.illustration} color="#6d28d9" className="guide-toc__illu" />
                            <span><strong>{String(index + 1).padStart(2, '0')} · {c.label}</strong><small>{c.detail}</small></span>
                            <ArrowRight size={17} aria-hidden="true" />
                        </button>
                    ))}
                </nav>

                {SECTIONS.map((s, i) => {
                    return (
                        <div key={s.key}>
                            {s.chapter && <h2 className="guide-chapter" id={s.chapterId}>{s.chapter}</h2>}
                            <section className={`guide-section${i % 2 === 1 ? ' guide-section--reverse' : ''}`} aria-labelledby={`guide-${s.key}`}>
                                <div className="guide-section__media">
                                    <GuideMedia base={`/guide/${s.key}`} illustration={s.illustration} caption={s.title} mediaType={s.media || 'image'} />
                                </div>
                                <div className="guide-section__body">
                                    <div className="guide-section__heading">
                                        <Illustration svg={s.illustration} color="#6d28d9" className="guide-section__illu" />
                                        <div>
                                            <span className="guide-section__eyebrow">{s.eyebrow}</span>
                                            <h3 id={`guide-${s.key}`}>{s.title}</h3>
                                        </div>
                                    </div>
                                    <p>{s.text}</p>
                                    <ul className="guide-list">
                                        {s.bullets.map((b, j) => (
                                            <li key={j}><Check size={17} style={{ color: s.color }} /> {b}</li>
                                        ))}
                                    </ul>
                                    {s.link && user && (
                                        <button type="button" className="btn btn-sm guide-section__link" style={{ '--guide-accent': s.color }} onClick={() => go(s.link.to)}>
                                            {s.link.label} <ArrowRight size={15} />
                                        </button>
                                    )}
                                </div>
                            </section>
                        </div>
                    );
                })}

                <div className="guide-cta">
                    <h2>Prêt à te lancer ?</h2>
                    <div className="guide-cta__buttons">
                        {user && firstVisit && presetScope ? (
                            /* 1re visite avec une thématique déjà posée (arrivée d'une landing
                               dédiée) : on l'emmène dedans plutôt que sur l'écran de config. */
                            <>
                                <button className="btn btn-primary guide-cta__btn" disabled={busy} onClick={() => go(`/compte/quiz/${presetScope}`)}>
                                    Commencer à m'entraîner <ArrowRight size={18} />
                                </button>
                                <button className="btn btn-secondary guide-cta__btn" disabled={busy} onClick={() => go('/compte/account/thematiques')}>
                                    <SlidersHorizontal size={18} /> Ajouter d'autres thématiques
                                </button>
                            </>
                        ) : user && firstVisit ? (
                            /* 1re visite : une seule prochaine action — configurer son espace
                               (un espace vide n'a rien à montrer tant que rien n'est configuré) */
                            <button className="btn btn-primary guide-cta__btn" disabled={busy} onClick={() => go('/compte/account/thematiques')}>
                                <SlidersHorizontal size={18} /> Configurer les thématiques de mon espace <ArrowRight size={18} />
                            </button>
                        ) : user ? (
                            <>
                                <button className="btn btn-primary guide-cta__btn" disabled={busy} onClick={() => go('/compte/quiz/mes-thematiques')}>
                                    Accéder à mon espace <ArrowRight size={18} />
                                </button>
                                <button className="btn btn-secondary guide-cta__btn" disabled={busy} onClick={() => go('/compte/account/thematiques')}>
                                    <SlidersHorizontal size={18} /> Configurer les thématiques de mon espace
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="btn btn-primary guide-cta__btn" onClick={() => navigate('/inscription')}>
                                    Créer mon compte gratuit <ArrowRight size={18} />
                                </button>
                                <button className="btn btn-secondary guide-cta__btn" onClick={() => navigate('/connexion')}>
                                    Se connecter
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {isPublicPage && <SiteFooter />}
        </div>
    );
}
